import { faX, faY } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';

import { FormFieldBooleanSelect } from 'src/features/parkingAdmin/components/Form/FormFieldBooleanSelect';
import { FormFieldInputText } from 'src/features/parkingAdmin/components/Form/FormFieldInputText';
import { type CreateParkingFormSchemaValues } from 'src/features/parkingAdmin/hooks/useCreateParking';
import parkingAdminMessages from 'src/features/parkingAdmin/utils/messages';
import { useGetCityList } from 'src/features/parkingManagement/api/useGetCityList';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { CURRENCIES } from 'src/features/payStations/types';
import { FormFieldBlock } from 'src/registry/components/blocks/FormFieldBlock';
import { InputWithIcon } from 'src/registry/components/inputs/InputWithIcon';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages, generalPriceMessages, generalValidationMessages } from 'src/utils/messages';

import type { UseFormReturn } from 'react-hook-form';
import type { City } from 'src/features/parkingManagement/types/parking';

interface AddNewParkingFormProps {
    form: UseFormReturn<CreateParkingFormSchemaValues>;
    onSubmit: () => void;
}

const AddNewParkingForm = ({ form, onSubmit }: AddNewParkingFormProps) => {
    const { formatMessage } = useIntl();

    const { data: cityList } = useGetCityList({});

    return (
        <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldInputText<CreateParkingFormSchemaValues>
                form={form}
                name="name"
                label={generalMessages.name}
                isRequired
            />
            <FormFieldInputText<CreateParkingFormSchemaValues>
                form={form}
                name="system_id"
                label={parkingAdminMessages.systemId}
                isRequired
            />
            <FormField
                control={form.control}
                name="serial_id"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(parkingAdminMessages.serialId)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={field.value ?? ''}
                                    disabled={true}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.country)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    placeholder={formatMessage(generalMessages.country)}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalMessages.city)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? undefined}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(generalValidationMessages.selectCity)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cityList?.results.map((city: City) => (
                                            <SelectItem key={city.id} value={city.id}>
                                                {city.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormFieldInputText<CreateParkingFormSchemaValues>
                form={form}
                name="address"
                label={generalMessages.address}
                isRequired
            />

            <FormField
                control={form.control}
                name="default_currency"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalPriceMessages.currency)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(val) => {
                                        field.onChange(val);
                                    }}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(generalValidationMessages.selectCurrency)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(CURRENCIES).map(([key, value]) => (
                                            <SelectItem key={key} value={value}>
                                                {value}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="number_of_floors"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(parkingAdminMessages.numberOfFloors)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={field.value ?? ''}
                                    placeholder="0"
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        field.onChange(digitsOnly ? parseInt(digitsOnly, 10) : '');
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="number_of_spaces"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(parkingAdminMessages.numberOfSpaces)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={field.value ?? ''}
                                    placeholder="0"
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                        field.onChange(digitsOnly ? parseInt(digitsOnly, 10) : '');
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormFieldBlock>
                <FormLabel variant="dialog">{formatMessage(parkingManagementMessages.geolocation)}</FormLabel>
                <FormField
                    control={form.control}
                    name="longitude"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <InputWithIcon
                                    icon={faX}
                                    inputMode="decimal"
                                    type="text"
                                    pattern="^-?\d*(\.?\d{0,16})?$"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="latitude"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <InputWithIcon
                                    icon={faY}
                                    inputMode="decimal"
                                    type="text"
                                    pattern="^-?\d*(\.?\d{0,16})?$"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </FormFieldBlock>

            <FormFieldBooleanSelect<CreateParkingFormSchemaValues>
                form={form}
                name="is_public"
                label={parkingAdminMessages.publicParking}
            />
            <FormFieldBooleanSelect<CreateParkingFormSchemaValues>
                form={form}
                name="is_featured"
                label={parkingAdminMessages.isFeatured}
            />
            <FormFieldBooleanSelect<CreateParkingFormSchemaValues>
                form={form}
                name="enable_online_payments"
                label={parkingAdminMessages.onlinePayments}
            />
            <FormFieldBooleanSelect<CreateParkingFormSchemaValues>
                form={form}
                name="enable_online_reservations"
                label={parkingAdminMessages.reservations}
            />
        </Form>
    );
};

export default AddNewParkingForm;
